import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import WishlistButton from '../components/WishlistButton';

const GameDetails = () => {
  const { id } = useParams();
  const [game, setGame] = useState(null);
  const [relatedVersions, setRelatedVersions] = useState([]);
  const [relatedGames, setRelatedGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGame = async () => {
      if (!id) {
        setError('No game ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.post('/query', {
          mainTable: 'games g',
          fields: [
            'g.id',
            'g.name',
            'g.first_release_date',
            'g.coverUrl',
            'g.trailerUrl',
            'g.storyline',
            'g.aggregated_rating',
            'g.rating_count',
            'GROUP_CONCAT(DISTINCT gen.name) as genres',
            'GROUP_CONCAT(DISTINCT t.name) as themes',
            'GROUP_CONCAT(DISTINCT ar.rating) as age_ratings',
            'GROUP_CONCAT(DISTINCT ls.language) as language_supports',
            's.steam_url'
          ],
          joins: [
            {
              type: 'LEFT',
              table: 'game_genres gg',
              on: 'g.id = gg.game_id'
            },
            {
              type: 'LEFT',
              table: 'genres gen',
              on: 'gg.genre_id = gen.id'
            },
            {
              type: 'LEFT',
              table: 'game_themes gt',
              on: 'g.id = gt.game_id'
            },
            {
              type: 'LEFT',
              table: 'themes t',
              on: 'gt.theme_id = t.id'
            },
            {
              type: 'LEFT',
              table: 'game_age_ratings gar',
              on: 'g.id = gar.game_id'
            },
            {
              type: 'LEFT',
              table: 'age_ratings ar',
              on: 'gar.age_rating_id = ar.id'
            },
            {
              type: 'LEFT',
              table: 'game_language_supports gls',
              on: 'g.id = gls.game_id'
            },
            {
              type: 'LEFT',
              table: 'language_supports ls',
              on: 'gls.language_support_id = ls.id'
            },
            {
              type: 'LEFT',
              table: 'steam_url_id s',
              on: 'g.id = s.game_id'
            }
          ],
          conditions: ['g.id = ?'],
          groupBy: ['g.id'],
          params: [id]
        });

        if (response.data && response.data.length > 0) {
          setGame(response.data[0]);
          
                     // Fetch related versions of the game
           const relatedResponse = await api.post('/query', {
             mainTable: 'games g',
             fields: [
               'g.id',
               'g.name',
               'g.coverUrl',
               'g.first_release_date'
             ],
             conditions: [
               'g.name LIKE ?',
               'g.id != ?'
             ],
             params: [`%${response.data[0].name.split(':')[0].trim()}%`, response.data[0].id]
           });
           
                       if (relatedResponse.data && relatedResponse.data.length > 0) {
              // Remove duplicates based on game name
              const uniqueVersions = relatedResponse.data.filter((version, index, self) => 
                index === self.findIndex(v => v.name === version.name)
              );
              setRelatedVersions(uniqueVersions);
            }
            
            // Fetch related games based on shared genres and themes
            const relatedGamesResponse = await api.post('/query', {
              mainTable: 'games g',
              fields: [
                'g.id',
                'g.name',
                'g.coverUrl',
                'g.aggregated_rating',
                'g.first_release_date',
                'GROUP_CONCAT(DISTINCT gen.name) as genres',
                'GROUP_CONCAT(DISTINCT t.name) as themes'
              ],
              joins: [
                {
                  type: 'LEFT',
                  table: 'game_genres gg',
                  on: 'g.id = gg.game_id'
                },
                {
                  type: 'LEFT',
                  table: 'genres gen',
                  on: 'gg.genre_id = gen.id'
                },
                {
                  type: 'LEFT',
                  table: 'game_themes gt',
                  on: 'g.id = gt.game_id'
                },
                {
                  type: 'LEFT',
                  table: 'themes t',
                  on: 'gt.theme_id = t.id'
                }
              ],
                             conditions: [
                 'g.id != ?',
                 'g.aggregated_rating IS NOT NULL',
                 '(EXISTS (SELECT 1 FROM game_genres gg2 JOIN genres gen2 ON gg2.genre_id = gen2.id WHERE gg2.game_id = g.id AND gen2.name IN (SELECT DISTINCT gen3.name FROM games g3 JOIN game_genres gg3 ON g3.id = gg3.game_id JOIN genres gen3 ON gg3.genre_id = gen3.id WHERE g3.id = ?)) OR EXISTS (SELECT 1 FROM game_themes gt2 JOIN themes t2 ON gt2.theme_id = t2.id WHERE gt2.game_id = g.id AND t2.name IN (SELECT DISTINCT t3.name FROM games g3 JOIN game_themes gt3 ON g3.id = gt3.game_id JOIN themes t3 ON gt3.theme_id = t3.id WHERE g3.id = ?)))'
               ],
              groupBy: ['g.id'],
              orderBy: ['g.aggregated_rating DESC'],
              limit: 8,
              params: [response.data[0].id, response.data[0].id, response.data[0].id]
            });
            
            if (relatedGamesResponse.data && relatedGamesResponse.data.length > 0) {
              setRelatedGames(relatedGamesResponse.data);
            }
        } else {
          setError('Game not found');
        }
      } catch (err) {
        console.error('Error fetching game:', err);
        setError('Failed to load game');
      } finally {
        setLoading(false);
      }
    };

    fetchGame();
  }, [id]);

  if (loading) {
    return (
      <div className="game-details-page bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-lg text-gray-300">Loading game...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="game-details-page bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <h1 className="text-3xl font-bold mb-4">Game Not Found</h1>
            <p className="text-lg text-gray-300 mb-8">{error}</p>
            <div className="bg-gray-800 rounded-lg p-8 max-w-md mx-auto">
              <div className="text-6xl mb-4">❌</div>
              <p className="text-gray-400">
                The game you're looking for doesn't exist or has been removed.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="game-details-page bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <h1 className="text-3xl font-bold mb-4">Game Not Found</h1>
            <p className="text-lg text-gray-300 mb-8">No game data available.</p>
            <div className="bg-gray-800 rounded-lg p-8 max-w-md mx-auto">
              <div className="text-6xl mb-4">🎮</div>
              <p className="text-gray-400">
                Please try searching for a different game.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatReleaseDate = (timestamp) => {
    if (!timestamp) return 'TBA';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
  };

  const formatRating = (rating) => {
    if (!rating) return 'N/A';
    return (rating / 10).toFixed(1);
  };

  const getAgeRatingDescription = (rating) => {
    if (!rating) return '';
    
    const ratingStr = rating.toString().trim();
    
    // PEGI ratings
    if (ratingStr === '3') return ' (Suitable for all ages)';
    if (ratingStr === '7') return ' (Suitable for 7 years and older)';
    if (ratingStr === '12') return ' (Suitable for 12 years and older)';
    if (ratingStr === '16') return ' (Suitable for 16 years and older)';
    if (ratingStr === '18') return ' (Suitable for 18 years and older)';
    
    // ESRB ratings
    if (ratingStr === 'E') return ' (Everyone)';
    if (ratingStr === 'E10+') return ' (Everyone 10+)';
    if (ratingStr === 'T') return ' (Teen)';
    if (ratingStr === 'M') return ' (Mature)';
    if (ratingStr === 'AO') return ' (Adults Only)';
    
    // If it's a number, assume PEGI
    if (!isNaN(ratingStr)) {
      return ` (PEGI ${ratingStr})`;
    }
    
    return '';
  };

  return (
    <div className="game-details-page bg-gray-900 text-white">
      <style jsx>{`
        .hexagon-container {
          position: relative;
          width: 100%;
          max-width: 800px;
          margin: 0 auto;
          aspect-ratio: 16/9;
        }
        
        .hexagon-clip {
          width: 100%;
          height: 100%;
          clip-path: polygon(15% 0%, 85% 0%, 100% 50%, 85% 100%, 15% 100%, 0% 50%);
          background: linear-gradient(45deg, #1f2937, #374151);
          padding: 4px;
          border-radius: 8px;
        }
        
        .hexagon-clip iframe {
          width: 100%;
          height: 100%;
          border-radius: 4px;
        }
        
        /* Add a subtle glow effect */
        .hexagon-container::before {
          content: '';
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          background: linear-gradient(45deg, #3b82f6, #8b5cf6, #10b981, #f59e0b);
          border-radius: 8px;
          z-index: -1;
          opacity: 0.3;
          filter: blur(4px);
        }
      `}</style>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="flex-1">
              {/* Game Header */}
              <div className="flex flex-col md:flex-row gap-8 mb-8">
                {/* Game Cover */}
                <div className="flex-shrink-0">
                  {game.coverUrl ? (
                    <img 
                      src={game.coverUrl} 
                      alt={game.name}
                      className="w-64 h-80 object-cover rounded-lg shadow-lg"
                    />
                  ) : (
                    <div className="w-64 h-80 bg-gray-700 rounded-lg flex items-center justify-center">
                      <span className="text-4xl">🎮</span>
                    </div>
                  )}
                </div>

                {/* Game Info */}
                <div className="flex-1">
                  <h1 className="text-4xl font-bold mb-4">{game.name}</h1>
                  
                  {/* Release Date */}
                  <div className="mb-4">
                    <span className="text-gray-400">Release Date: </span>
                    <span className="font-semibold">{formatReleaseDate(game.first_release_date)}</span>
                  </div>

                  {/* Rating */}
                  {game.aggregated_rating && (
                    <div className="mb-4">
                      <span className="text-gray-400">Rating: </span>
                      <span className="font-semibold">{formatRating(game.aggregated_rating)}/10</span>
                      {game.rating_count && (
                        <span className="text-gray-500 ml-2">({game.rating_count} ratings)</span>
                      )}
                    </div>
                  )}

                  {/* Genres */}
                  {game.genres && (
                    <div className="mb-4">
                      <span className="text-gray-400">Genres: </span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {[...new Set(game.genres.split(',').map(g => g.trim()))].map((genre, index) => (
                          <span key={index} className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
                            {genre}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                                                        {/* Themes */}
                    {game.themes && (
                      <div className="mb-4">
                        <span className="text-gray-400">Themes: </span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {[...new Set(game.themes.split(',').map(t => t.trim()))].map((theme, index) => (
                            <span key={index} className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm">
                              {theme}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                                     {/* Age Ratings */}
                   {game.age_ratings && (
                     <div className="mb-4">
                       <span className="text-gray-400">Age Rating: </span>
                       <span className="font-semibold bg-orange-600 text-white px-2 py-1 rounded text-sm">
                         {game.age_ratings.split(',')[0].trim()}
                       </span>
                       <span className="text-gray-500 text-sm ml-2">
                         {getAgeRatingDescription(game.age_ratings.split(',')[0].trim())}
                       </span>
                       <span className="relative inline-block ml-2 group">
                         <span className="text-blue-400 hover:text-blue-300 cursor-help text-sm">ⓘ</span>
                         <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-96 bg-gray-800 border border-gray-600 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                           <div className="p-4">
                             <h4 className="font-semibold text-white mb-3 text-center">Age Rating Systems</h4>
                             <div className="space-y-3 text-xs">
                               {/* PEGI */}
                               <div>
                                 <h5 className="font-medium text-blue-300 mb-1">PEGI (Europe)</h5>
                                 <div className="grid grid-cols-2 gap-1 text-gray-300">
                                   <div>3 - Suitable for all ages</div>
                                   <div>7 - 7 years and older</div>
                                   <div>12 - 12 years and older</div>
                                   <div>16 - 16 years and older</div>
                                   <div>18 - 18 years and older</div>
                                 </div>
                               </div>
                               {/* ESRB */}
                               <div>
                                 <h5 className="font-medium text-green-300 mb-1">ESRB (North America)</h5>
                                 <div className="grid grid-cols-2 gap-1 text-gray-300">
                                   <div>E - Everyone</div>
                                   <div>E10+ - Everyone 10+</div>
                                   <div>T - Teen</div>
                                   <div>M - Mature</div>
                                   <div>AO - Adults Only</div>
                                 </div>
                               </div>
                               {/* CERO */}
                               <div>
                                 <h5 className="font-medium text-yellow-300 mb-1">CERO (Japan)</h5>
                                 <div className="grid grid-cols-2 gap-1 text-gray-300">
                                   <div>A - All ages</div>
                                   <div>B - 12 years and older</div>
                                   <div>C - 15 years and older</div>
                                   <div>D - 17 years and older</div>
                                   <div>Z - 18 years and older</div>
                                 </div>
                               </div>
                               {/* USK */}
                               <div>
                                 <h5 className="font-medium text-purple-300 mb-1">USK (Germany)</h5>
                                 <div className="grid grid-cols-2 gap-1 text-gray-300">
                                   <div>0 - No age restriction</div>
                                   <div>6 - 6 years and older</div>
                                   <div>12 - 12 years and older</div>
                                   <div>16 - 16 years and older</div>
                                   <div>18 - 18 years and older</div>
                                 </div>
                               </div>
                             </div>
                           </div>
                           {/* Arrow */}
                           <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                         </div>
                       </span>
                     </div>
                   )}

                   {/* Action Buttons */}
                   <div className="mb-4">
                     <div className="flex items-center gap-2">
                       <WishlistButton 
                         gameId={game.id}
                         releaseDate={game.first_release_date}
                       />
                       {game.steam_url && (
                         <a 
                           href={game.steam_url}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="inline-flex items-center justify-center px-4 py-2 bg-[#1b2838] hover:bg-[#2a3f5a] text-white text-sm font-medium rounded-md transition-colors duration-200"
                         >
                           <svg className="w-5 h-5 mr-2" viewBox="0 0 16 16" fill="currentColor">
                             <path d="M8 0C3.582 0 0 3.582 0 8c0 3.727 2.551 6.85 6 7.744V14h-.5A1.5 1.5 0 004 15.5v.5h8v-.5a1.5 1.5 0 00-1.5-1.5H10v-2.256C13.449 14.85 16 11.727 16 8c0-4.418-3.582-8-8-8zm5.845 11.235l-2.194.893c-.148.574-.625.996-1.226.996a1.32 1.32 0 01-1.323-1.317c0-.043.001-.087.006-.13l-1.422-.58c.021-.084.031-.17.031-.259 0-.75-.6-1.354-1.343-1.354-.653 0-1.195.462-1.324 1.073l-1.66-.675c.061-.988.74-1.82 1.64-1.966l2.02-.826c.23-.636.853-1.09 1.58-1.09.737 0 1.369.472 1.586 1.122l2.198.898c.03.93.052.188.052.288 0 .895-.711 1.621-1.619 1.621-.473 0-.896-.207-1.186-.535l-1.564.638c.033.142.052.289.052.441 0 .973-.77 1.769-1.748 1.769-.878 0-1.584-.635-1.735-1.472l-.851-.349c.274-.695.953-1.189 1.752-1.189.31 0 .6.076.855.209l1.797.733c.132-.413.516-.713.974-.713.566 0 1.024.458 1.024 1.024 0 .153-.033.299-.094.428l1.77.728c.009-.134.036-.263.036-.401 0-.753-.608-1.343-1.363-1.343-.16 0-.314.03-.457.082l-1.731-.708c-.111.488-.39.901-.78 1.158z"/>
                           </svg>
                           View on Steam
                         </a>
                       )}
                     </div>
                   </div>

                  {/* Language Support */}
                  {game.language_supports && (
                    <div className="mb-4">
                      <span className="text-gray-400">Languages: </span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {game.language_supports.split(',').slice(0, 5).map((lang, index) => (
                          <span key={index} className="bg-gray-600 text-white px-2 py-1 rounded text-xs">
                            {lang.trim()}
                          </span>
                        ))}
                        {game.language_supports.split(',').length > 5 && (
                          <span className="text-gray-500 text-xs">+{game.language_supports.split(',').length - 5} more</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

                                             {/* Trailer */}
                {game.trailerUrl && (
                  <div className="mb-8">
                    <div className="relative">
                     {/* Unique hexagon-shaped container */}
                     <div className="hexagon-container">
                       <div className="hexagon-clip">
                         <iframe
                           src={game.trailerUrl.replace('watch?v=', 'embed/')}
                           title={`${game.name} Trailer`}
                           className="w-full h-full"
                           frameBorder="0"
                           allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                           allowFullScreen
                         ></iframe>
                       </div>
                     </div>
                     

                   </div>
                 </div>
               )}

               {/* Game Description */}
               {game.storyline && (
                 <div className="mb-8">
                   <h2 className="text-2xl font-bold mb-4">About This Game</h2>
                   <p className="text-gray-300 leading-relaxed">{game.storyline}</p>
                 </div>
               )}

              {/* Related Versions */}
              {relatedVersions.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold mb-4">Other Versions</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {relatedVersions.map((version) => (
                      <div 
                        key={version.id} 
                        className="bg-gray-800 rounded-lg p-4 hover:bg-gray-700 transition-colors cursor-pointer"
                        onClick={() => window.location.href = `/game-details/${version.id}`}
                      >
                        <div className="flex items-center space-x-3">
                          {version.coverUrl ? (
                            <img 
                              src={version.coverUrl} 
                              alt={version.name}
                              className="w-16 h-20 object-cover rounded"
                            />
                          ) : (
                            <div className="w-16 h-20 bg-gray-700 rounded flex items-center justify-center">
                              <span className="text-2xl">🎮</span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-sm truncate">{version.name}</h3>
                            {version.first_release_date && (
                              <p className="text-xs text-gray-400">
                                {new Date(version.first_release_date * 1000).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Related Games Sidebar */}
            {relatedGames.length > 0 && (
              <div className="lg:w-80 flex-shrink-0">
                <div className="sticky top-8">
                  <h2 className="text-2xl font-bold mb-4">Related Games</h2>
                  <div className="space-y-4">
                    {relatedGames.map((relatedGame) => (
                      <div 
                        key={relatedGame.id} 
                        className="bg-gray-800 rounded-lg p-4 hover:bg-gray-700 transition-colors cursor-pointer"
                        onClick={() => window.location.href = `/game-details/${relatedGame.id}`}
                      >
                        <div className="flex items-start space-x-3">
                          {relatedGame.coverUrl ? (
                            <img 
                              src={relatedGame.coverUrl} 
                              alt={relatedGame.name}
                              className="w-16 h-20 object-cover rounded flex-shrink-0"
                            />
                          ) : (
                            <div className="w-16 h-20 bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                              <span className="text-2xl">🎮</span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-sm truncate mb-1">{relatedGame.name}</h3>
                            {relatedGame.aggregated_rating && (
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="text-yellow-400 text-sm">★</span>
                                <span className="text-sm text-gray-300">
                                  {(relatedGame.aggregated_rating / 10).toFixed(1)}
                                </span>
                              </div>
                            )}
                            {relatedGame.first_release_date && (
                              <p className="text-xs text-gray-400 mb-2">
                                {new Date(relatedGame.first_release_date * 1000).toLocaleDateString()}
                              </p>
                            )}
                            {relatedGame.genres && (
                              <div className="flex flex-wrap gap-1">
                                {[...new Set(relatedGame.genres.split(',').map(g => g.trim()))].slice(0, 2).map((genre, index) => (
                                  <span key={index} className="bg-blue-600 text-white px-2 py-1 rounded text-xs">
                                    {genre}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameDetails; 